import { Injectable } from '@angular/core';
import { Namespace, Workflow } from 'enums-lib';
import { IEntityLabelPair } from './entity-type.service';

enum InveniasEntityToPickerTypes {
  peopleListItem = 'People',
  companiesListItem = 'Companies',
  assignmentsListItem = 'Assignments',
  programmesListItem = 'Programmes',
}

enum InveniasPickerTypeToEntity {
  People = 'peopleListItem',
  Companies = 'companiesListItem',
  Assignments = 'assignmentsListItem',
  Programmes = 'programmesListItem',
}

export interface IDestinationsForOutputFileType {
  type: string;
  documentType: string;
  entityTypes: string[];
}

@Injectable({ providedIn: 'root' })
export class EntityTypeServiceFactory {
  getRecordTypeLabels(namespace: string | Namespace): {
    choose: { title: string; tooltip: string };
    chosen: { title: string };
  } {
    if (namespace === Namespace.invenias) {
      return {
        choose: {
          title: 'Choose a record type',
          tooltip: 'One record type must be selected',
        },
        chosen: { title: 'Chosen record type' },
      };
    }

    if (namespace === Namespace.bullhorn || namespace === Namespace.salesforce) {
      return {
        choose: {
          title: 'Choose an entity type',
          tooltip: 'One entity type must be selected',
        },
        chosen: { title: 'Chosen entity type' },
      };
    }

    throw new Error(`Unknown '${namespace}' getting record labels.`);
  }

  getRecordTerm(namespace: Namespace): string {
    if (namespace === Namespace.bullhorn || namespace === Namespace.salesforce) {
      return 'Entity';
    }

    if (namespace === Namespace.invenias) {
      return 'Record';
    }

    throw new Error(`Unknown '${namespace}' getting record term.`);
  }

  /**
   * Namespace specific re-mapping of entities. For example, with Invenias the objectName (e.g. "peopleListItem" becomes "People").
   * @param namespace the namespace identifier
   * @param entities the list of entities to re-map
   * @return the re-mapped list of entities
   */
  getMappedEntityTypes(namespace: string | Namespace, entities: IEntityLabelPair[]): IEntityLabelPair[] {
    if (namespace === Namespace.bullhorn || namespace === Namespace.salesforce) {
      return entities;
    }

    if (namespace === Namespace.invenias) {
      return entities.map((elp) => {
        return {
          ...elp,
          objectName: InveniasEntityToPickerTypes[elp.objectName],
        };
      });
    }

    throw new Error(`No entity type mapping found for '${namespace}'`);
  }

  entityTypeHasAvailabilityOptions(namespace: string | Namespace, templateEntityName: string, workflowType: Workflow): boolean {
    if (namespace === Namespace.bullhorn) {
      switch (workflowType) {
        case Workflow.DocumentMerge:
          return ['ClientContact'].includes(templateEntityName);
        case Workflow.SimpleDocx:
          return ['Candidate', 'ClientContact'].includes(templateEntityName);
        default:
          return false;
      }
    }
    if (namespace === Namespace.invenias) {
      return false;
    }
    // @TODO: check if this exists in BH4SF
    if (namespace === Namespace.salesforce) {
      return false;
    }

    throw new Error(`No availability options found for '${namespace}'`);
  }

  getTemplateAvailabilityEntityTypes(
    namespace: string | Namespace,
    entities: ({ tooltip: string } & IEntityLabelPair)[],
    templateEntityName: string
  ): ({ tooltip: string } & IEntityLabelPair)[] {
    if (namespace === Namespace.bullhorn) {
      switch (templateEntityName) {
        case 'Candidate':
          return entities.filter((x) => x.objectName === 'Candidate' || x.objectName === 'JobOrder');
        case 'ClientContact':
          return entities.filter((x) => x.objectName === 'ClientContact' || x.objectName === 'ClientCorporation');
        default:
          return [];
      }
    }

    if (namespace === Namespace.invenias) {
      return [];
    }
    // @TODO: check if exists in BH4SF
    if (namespace === Namespace.salesforce) {
      return [];
    }

    throw new Error(`No availability entity types found for '${namespace}'`);
  }

  /**
   * Namespace specific entity name lookup based on the supplied name. For example, in Invenias "People" will become "peopleListItem".
   * @param namespace the namespace identifier
   * @param pickerName the pickerName to lookup (e.g. People)
   * @return the corresponding entity name  (e.g. peopleListItem)
   */
  lookupEntityName(namespace: string | Namespace, pickerName: string): string {
    if (namespace !== Namespace.invenias) {
      return pickerName;
    }

    // entityName will be Assignments, Companies, People, etc.
    const isExpectedValue = pickerName in InveniasPickerTypeToEntity;
    const newName = isExpectedValue ? InveniasPickerTypeToEntity[pickerName] : pickerName;
    if (!(newName in InveniasEntityToPickerTypes)) {
      console.warn(`'${newName}' from '${pickerName}' is not in the valid set of entity names.`);
    }
    return newName;
  }
}
